// Overall Analysis JavaScript

let userData = null;
let allTestsData = [];
let currentChart = null; 
let chartData = null; 

// Initialize on page load
document.addEventListener('DOMContentLoaded', async () => {
    try {
        console.log('🚀 Overall Analysis page loading...');
        
        // Check authentication
        userData = getUserSession();
        if (!userData) {
            window.location.href = '/pages/act-login';
            return;
        }

        // Load all tests data
        await loadAllTestsData();

        // Add chart selector event listener
        document.getElementById('chartSelector').addEventListener('change', (e) => {
            renderSelectedChart(e.target.value);
        });

    } catch (error) {
        console.error('❌ Error initializing:', error);
        hideLoading();
        alert('Error loading overall analysis: ' + error.message);
    }
});

// Turn "4", "4/36", "4 out of 36" → 4
function scoreToNumber(v) {
  if (typeof v === 'number') return v;
  if (v == null) return 0;

  const s = String(v).trim().toLowerCase();

  // match "12/36" or "12 out of 36"
  let m = s.match(/(\d+)\s*(?=\/|\s*out\s*of\b)/);
  if (m) return Number(m[1]);

  // fallback: leading number
  m = s.match(/^(\d+)/);
  return m ? Number(m[1]) : 0;
}

// Pick the first existing field from the response
function pickField(obj, names, fallback = 0) {
  for (const name of names) {
    if (obj && obj[name] !== undefined && obj[name] !== null && obj[name] !== '') {
      return obj[name];
    }
  }
  return fallback;
}

// Format test name: "test_1" or "Test test_1" → "Test 1"
function formatTestName(name) {
  if (!name) return 'Test';
  
  const str = String(name);
  
  // Extract number from patterns like "test_1", "Test test_1", "test 1", etc.
  const match = str.match(/(\d+)/);
  if (match) {
    return `Test ${match[1]}`;
  }
  
  // If no number found, return cleaned version
  return str.replace(/test_?/gi, 'Test ').trim();
}



// Load and aggregate all test data
async function loadAllTestsData() {
    showLoading();

    try {
        console.log('📊 Loading all tests data...');

        // Get test history
        const historyResponse = await SheetsAPI.getStudentTestHistory({ uid: userData.uid });
        
        if (!historyResponse || !historyResponse.success) {
            throw new Error('No test history found');
        }

        const tests = historyResponse.data || [];
        const submittedTests = tests.filter(t => t.status === 'submitted');

        console.log(`Found ${submittedTests.length} submitted tests`);

        if (submittedTests.length === 0) {
            showNoData();
            hideLoading();
            return;
        }

        // Update test count
        document.getElementById('testCount').textContent = 
            `Analyzing ${submittedTests.length} completed test${submittedTests.length > 1 ? 's' : ''}`;

        // Load detailed data for each test
        allTestsData = [];
        
        for (const test of submittedTests) {
            try {
                // Get test responses
                const responsesResponse = await SheetsAPI.getStudentResponses({
                    uid: userData.uid,
                    test_id: test.test_id
                });

                if (!responsesResponse || !responsesResponse.success) continue;

                const studentData = responsesResponse.data;

                // Get answer keys
                const keysResponse = await SheetsAPI.getAnswerKeys(test.test_id);
                if (!keysResponse || !keysResponse.success) continue;

                const answerKeys = parseAnswerKeys(keysResponse.answer_keys);

                // Parse student answers
                const studentAnswers = {
                    english: JSON.parse(studentData.english_answers || '{}'),
                    math: JSON.parse(studentData.math_answers || '{}'),
                    reading: JSON.parse(studentData.reading_answers || '{}')
                };

                // try different key names because SheetsAPI may send camelCase or snake_case
                const rawComposite = pickField(studentData, [
                'composite_score',
                'compositeScore',
                'composite',
                'total_score'
                ]);

                const rawEnglish = pickField(studentData, [
                'english_score',
                'englishScore',
                'english'
                ]);

                const rawMath = pickField(studentData, [
                'math_score',
                'mathScore',
                'math'
                ]);

                const rawReading = pickField(studentData, [
                'reading_score',
                'readingScore',
                'reading'
                ]);

                // log once so we can see real values coming from the sheet
                console.log('Raw scores from sheet for', test.test_id, {
                rawComposite,
                rawEnglish,
                rawMath,
                rawReading
                });

                const testAnalysis = calculateTestAnalysis(answerKeys, studentAnswers, {
                testId: test.test_id,
                testName: formatTestName(test.test_name || test.test_id),
                composite: scoreToNumber(rawComposite),
                englishScore: scoreToNumber(rawEnglish),
                mathScore: scoreToNumber(rawMath),
                readingScore: scoreToNumber(rawReading),
                date: test.submitted_at || test.created_at
                });

                allTestsData.push(testAnalysis);

            } catch (error) {
                console.error(`Error loading test ${test.test_id}:`, error);
            }
        }

        console.log('✅ All tests data loaded:', allTestsData);

        if (allTestsData.length === 0) {
            showNoData();
            hideLoading();
            return;
        }

        // Log the first test to see structure
        console.log('First test sample:', JSON.stringify(allTestsData[0], null, 2));

        // Aggregate data and render
        const aggregatedData = aggregateAllData(allTestsData);
        console.log('Aggregated data ready:', aggregatedData);
        
        renderAllCharts(aggregatedData);

        // Show content
        document.getElementById('contentContainer').style.display = 'block';
        hideLoading();

    } catch (error) {
        console.error('❌ Error in loadAllTestsData:', error);
        hideLoading();
        throw error;
    }
}

// Parse answer keys from API response
function parseAnswerKeys(keys) {
    const result = {
        english: {},
        math: {},
        reading: {}
    };

    keys.forEach(row => {
        const section = String(row.section || '').toLowerCase();
        const qNum = Number(row.question_number);
        
        if ((section === 'english' || section === 'math' || section === 'reading') && qNum > 0) {
            result[section][qNum] = {
                correct_answer: String(row.correct_answer || '').trim().toUpperCase(),
                difficulty: String(row.difficulty || 'medium').toLowerCase(),
                domain: String(row.domain || 'General').trim(),
                is_scored: row.is_scored !== false && row.is_scored !== 'FALSE'
            };
        }
    });

    return result;
}

// Calculate analysis for a single test
function calculateTestAnalysis(answerKeys, studentAnswers, testInfo) {
    const analysis = {
        testId: testInfo.testId,
        testName: testInfo.testName,
        date: testInfo.date,
        // we'll fix this below if it's 0/undefined
        composite: testInfo.composite,
        sections: {
            english: initSectionAnalysis('English', testInfo.englishScore),
            math: initSectionAnalysis('Math', testInfo.mathScore),
            reading: initSectionAnalysis('Reading', testInfo.readingScore)
        }
    };

    // 1. process all questions → fills correct/incorrect/total
    ['english', 'math', 'reading'].forEach(section => {
        processSection(section, answerKeys[section], studentAnswers[section], analysis.sections[section]);
    });

    // 2. Scale section scores to ACT 1-36 range
    ['english', 'math', 'reading'].forEach(section => {
        const sec = analysis.sections[section];
        if (sec.score == null || sec.score === 0) {
            // Convert raw correct answers to ACT scaled score (1-36)
            // Formula: scale correct answers proportionally to 1-36 range
            if (sec.total > 0) {
                const percentage = sec.correct / sec.total;
                sec.score = Math.max(1, Math.min(36, Math.round(percentage * 36)));
            } else {
                sec.score = 1;
            }
        } else {
            // Ensure sheet scores are within ACT range
            sec.score = Math.max(1, Math.min(36, sec.score));
        }
    });

    // 3. if composite wasn't provided (0 in your console), calculate average of section scores
    if (!analysis.composite || analysis.composite === 0) {
        const totalScore = 
            analysis.sections.english.score +
            analysis.sections.math.score +
            analysis.sections.reading.score;
        // ACT composite is the average of sections, rounded to nearest whole number
        analysis.composite = Math.round(totalScore / 3);
    }
    
    // Ensure composite score never exceeds 36 (ACT scale)
    analysis.composite = Math.min(36, Math.max(1, analysis.composite));

    return analysis;
}


// Initialize section analysis structure
function initSectionAnalysis(name, score) {
    return {
        name,
        score,
        correct: 0,
        incorrect: 0,
        total: 0,
        accuracy: 0,
        difficulty: {
            easy: { correct: 0, incorrect: 0, total: 0 },
            medium: { correct: 0, incorrect: 0, total: 0 },
            hard: { correct: 0, incorrect: 0, total: 0 }
        },
        domains: {}
    };
}

// Process a single section
function processSection(sectionName, keys, answers, sectionData) {
    Object.keys(keys).forEach(qNum => {
        const keyData = keys[qNum];
        
        // Include ALL questions in analysis (both scored and unscored)
        sectionData.total++;
        
        const studentAns = answers[qNum];
        const isCorrect = studentAns && studentAns === keyData.correct_answer;
        
        if (isCorrect) {
            sectionData.correct++;
        } else {
            sectionData.incorrect++;
        }

        // Track difficulty
        const diff = keyData.difficulty;
        if (sectionData.difficulty[diff]) {
            sectionData.difficulty[diff].total++;
            if (isCorrect) {
                sectionData.difficulty[diff].correct++;
            } else {
                sectionData.difficulty[diff].incorrect++;
            }
        }

        // Track domain
        const domain = keyData.domain;
        if (!sectionData.domains[domain]) {
            sectionData.domains[domain] = {
                correct: 0,
                incorrect: 0,
                total: 0
            };
        }
        sectionData.domains[domain].total++;
        
        if (isCorrect) {
            sectionData.domains[domain].correct++;
        } else {
            sectionData.domains[domain].incorrect++;
        }
    });

    
    // Calculate section accuracy
sectionData.accuracy = sectionData.total > 0 
    ? Math.round((sectionData.correct / sectionData.total) * 100) 
    : 0;
}

// Aggregate data from all tests
function aggregateAllData(testsData) {
    console.log('Aggregating data from tests:', testsData);

    const aggregated = {
        tests: testsData.map(t => {
            console.log('Processing test:', t.testName, 'Composite:', t.composite, 'English:', t.sections.english.score);
            return {
                testName: t.testName,
                composite: Math.min(36, Math.max(1, t.composite || 1)),
                englishScore: Math.min(36, Math.max(1, t.sections.english.score || 1)),
                mathScore: Math.min(36, Math.max(1, t.sections.math.score || 1)),
                readingScore: Math.min(36, Math.max(1, t.sections.reading.score || 1))
            };
        }),
        sections: {
            english: aggregateSection(testsData, 'english'),
            math: aggregateSection(testsData, 'math'),
            reading: aggregateSection(testsData, 'reading')
        }
    };

    console.log('Aggregated data:', aggregated);
    return aggregated;
}

// Aggregate data for a single section across all tests
function aggregateSection(testsData, sectionKey) {
    const aggregated = {
        correct: 0,
        incorrect: 0,
        total: 0,
        accuracy: 0,
        difficulty: {
            easy: { correct: 0, incorrect: 0, total: 0 },
            medium: { correct: 0, incorrect: 0, total: 0 },
            hard: { correct: 0, incorrect: 0, total: 0 }
        },
        domains: {}
    };

    testsData.forEach(test => {
        const section = test.sections[sectionKey];
        
        // Aggregate basic stats
        aggregated.correct += section.correct;
        aggregated.incorrect += section.incorrect;
        aggregated.total += section.total;

        // Aggregate difficulty
        ['easy', 'medium', 'hard'].forEach(diff => {
            aggregated.difficulty[diff].correct += section.difficulty[diff].correct;
            aggregated.difficulty[diff].incorrect += section.difficulty[diff].incorrect;
            aggregated.difficulty[diff].total += section.difficulty[diff].total;
        });

        // Aggregate domains
        Object.keys(section.domains).forEach(domain => {
            if (!aggregated.domains[domain]) {
                aggregated.domains[domain] = {
                    correct: 0,
                    incorrect: 0,
                    total: 0
                };
            }
            aggregated.domains[domain].correct += section.domains[domain].correct;
            aggregated.domains[domain].incorrect += section.domains[domain].incorrect;
            aggregated.domains[domain].total += section.domains[domain].total;
        });
    });

    // Calculate overall accuracy
    aggregated.accuracy = aggregated.total > 0 
        ? Math.round((aggregated.correct / aggregated.total) * 100) 
        : 0;

    return aggregated;
}

// Render all charts and data
function renderAllCharts(data) {
    // Store data globally for chart switching
    chartData = data;

    console.log('📊 Rendering charts with data:', chartData);

    // 1. Render initial score chart (Total Scores)
    renderSelectedChart('totalScores');

    // 2. Answer Accuracy Stats
    renderAccuracyStats(data.sections);

    // 3. Difficulty-wise Performance
    renderDifficultyCharts(data.sections);

    // 4. Domain-wise Accuracy
    renderDomainBars(data.sections);
}

// Render selected chart based on dropdown
function renderSelectedChart(chartType) {
    console.log('Rendering chart type:', chartType);
    console.log('Chart data available:', chartData);

    if (!chartData || !chartData.tests || chartData.tests.length === 0) {
        console.error('No chart data available!');
        return;
    }

    // Destroy existing chart
    if (currentChart) {
        currentChart.destroy();
        currentChart = null;
    }

    // Update title
    const titles = {
        'totalScores': 'Composite Score (Scaled 1-36)',
        'allScoresBar': 'All Scores (Bar View)',
        'allScoresLine': 'All Scores (Line View)',
        'englishScores': 'English Scores',
        'mathScores': 'Math Scores',
        'readingScores': 'Reading Scores'
    };
    document.getElementById('chartTitle').textContent = titles[chartType];

    // Render appropriate chart
    switch(chartType) {
        case 'totalScores':
            renderTotalScoresChart();
            break;
        case 'allScoresBar':
            renderAllScoresBarChart();
            break;
        case 'allScoresLine':
            renderAllScoresLineChart();
            break;
        case 'englishScores':
            renderSectionScoresChart('english', '#10b981');
            break;
        case 'mathScores':
            renderSectionScoresChart('math', '#f59e0b');
            break;
        case 'readingScores':
            renderSectionScoresChart('reading', '#8b5cf6');
            break;
    }
}

// 1. Total Scores Chart - Composite scores calculated as average of section scores
function renderTotalScoresChart() {
    if (!chartData || !chartData.tests) {
        console.error('No chart data available for Total Scores chart');
        return;
    }

    const labels = chartData.tests.map(t => t.testName);
    
    // Calculate composite scores as average of section scores (scaled to 1-36)
    const compositeScores = chartData.tests.map(t => {
        // Average the three section scores
        const avgScore = Math.round((t.englishScore + t.mathScore + t.readingScore) / 3);
        // Cap at 36 (ACT max score)
        return Math.min(36, avgScore);
    });

    console.log('Total Scores (Composite) - Labels:', labels);
    console.log('Total Scores (Composite) - Scores:', compositeScores);

    currentChart = new Chart(document.getElementById('mainChart'), {
        type: 'bar',
        data: {
            labels: labels,
            datasets: [{
                label: 'Composite Score',
                data: compositeScores,
                backgroundColor: '#1e3a5f',
                borderRadius: 6,
                barThickness: 80
            }]
        },
        options: {
            responsive: true,
            maintainAspectRatio: false,
            scales: {
                y: {
                    beginAtZero: true,
                    max: 36, // ACT composite score max is 36
                    ticks: { stepSize: 6 },
                    grid: { color: '#e5e7eb' }
                },
                x: {
                    grid: { display: false }
                }
            },
            plugins: {
                legend: { display: false },
                tooltip: {
                    callbacks: {
                        label: function(context) {
                            return `Composite: ${context.parsed.y}/36`;
                        }
                    }
                }
            }
        },
        plugins: [{
            id: 'barLabels',
            afterDatasetsDraw: (chart) => {
                const ctx = chart.ctx;
                chart.data.datasets.forEach((dataset, i) => {
                    const meta = chart.getDatasetMeta(i);
                    meta.data.forEach((bar, index) => {
                        const data = dataset.data[index];
                        if (data && data > 0) {
                            ctx.fillStyle = '#1e293b';
                            ctx.font = 'bold 14px sans-serif';
                            ctx.textAlign = 'center';
                            ctx.fillText(data, bar.x, bar.y - 5);
                        }
                    });
                });
            }
        }]
    });

    console.log('Total Scores chart rendered successfully with composite scores');
}

// 2. All Scores Bar Chart - Grouped bars for each section (English, Math, Reading only)
function renderAllScoresBarChart() {
    const labels = chartData.tests.map(t => t.testName);
    // Cap all scores at 36 to prevent overflow
    const englishScores = chartData.tests.map(t => Math.min(36, t.englishScore));
    const mathScores = chartData.tests.map(t => Math.min(36, t.mathScore));
    const readingScores = chartData.tests.map(t => Math.min(36, t.readingScore));

    currentChart = new Chart(document.getElementById('mainChart'), {
        type: 'bar',
        data: {
            labels: labels,
            datasets: [
                {
                    label: 'English',
                    data: englishScores,
                    backgroundColor: '#6b9bc3',
                    borderRadius: 6
                },
                {
                    label: 'Math',
                    data: mathScores,
                    backgroundColor: '#4db8e8',
                    borderRadius: 6
                },
                {
                    label: 'Reading',
                    data: readingScores,
                    backgroundColor: '#a8d5e2',
                    borderRadius: 6
                }
            ]
        },
        options: {
            responsive: true,
            maintainAspectRatio: false,
            scales: {
                y: {
                    beginAtZero: true,
                    max: 36, // ACT section score max is 36
                    ticks: { stepSize: 6 },
                    grid: { color: '#e5e7eb' }
                },
                x: {
                    grid: { display: false }
                }
            },
            plugins: {
                legend: {
                    display: true,
                    position: 'bottom',
                    labels: {
                        usePointStyle: true,
                        padding: 15
                    }
                }
            }
        },
        plugins: [{
            id: 'barLabels',
            afterDatasetsDraw: (chart) => {
                const ctx = chart.ctx;
                chart.data.datasets.forEach((dataset, i) => {
                    const meta = chart.getDatasetMeta(i);
                    meta.data.forEach((bar, index) => {
                        const data = dataset.data[index];
                        if (data > 0) {
                            ctx.fillStyle = '#1e293b';
                            ctx.font = 'bold 11px sans-serif';
                            ctx.textAlign = 'center';
                            ctx.fillText(data, bar.x, bar.y - 5);
                        }
                    });
                });
            }
        }]
    });
}

// 3. All Scores Line Chart - Multiple lines for each section (English, Math, Reading only)
function renderAllScoresLineChart() {
    const labels = chartData.tests.map(t => t.testName);
    // Cap all scores at 36 to prevent overflow
    const englishScores = chartData.tests.map(t => Math.min(36, t.englishScore));
    const mathScores = chartData.tests.map(t => Math.min(36, t.mathScore));
    const readingScores = chartData.tests.map(t => Math.min(36, t.readingScore));

    currentChart = new Chart(document.getElementById('mainChart'), {
        type: 'line',
        data: {
            labels: labels,
            datasets: [
                {
                    label: 'English',
                    data: englishScores,
                    borderColor: '#6b9bc3',
                    backgroundColor: 'rgba(107, 155, 195, 0.1)',
                    borderWidth: 3,
                    tension: 0.4,
                    fill: false,
                    pointRadius: 5,
                    pointHoverRadius: 7
                },
                {
                    label: 'Math',
                    data: mathScores,
                    borderColor: '#4db8e8',
                    backgroundColor: 'rgba(77, 184, 232, 0.1)',
                    borderWidth: 3,
                    tension: 0.4,
                    fill: false,
                    pointRadius: 5,
                    pointHoverRadius: 7
                },
                {
                    label: 'Reading',
                    data: readingScores,
                    borderColor: '#a8d5e2',
                    backgroundColor: 'rgba(168, 213, 226, 0.1)',
                    borderWidth: 3,
                    tension: 0.4,
                    fill: false,
                    pointRadius: 5,
                    pointHoverRadius: 7
                }
            ]
        },
        options: {
            responsive: true,
            maintainAspectRatio: false,
            layout: {
                padding: {
                    top: 10,
                    bottom: 0,
                    left: 0,
                    right: 0
                }
            },
            scales: {
                y: {
                    beginAtZero: true,
                    max: 36,
                    ticks: { 
                        stepSize: 6,
                        padding: 5
                    },
                    grid: { color: '#e5e7eb' }
                },
                x: {
                    grid: { display: false }
                }
            },
            plugins: {
                legend: {
                    display: true,
                    position: 'bottom',
                    labels: {
                        usePointStyle: true,
                        padding: 15
                    }
                }
            }
        }
    });
}

// 4. Individual Section Scores - Combined Bar + Line Chart
function renderSectionScoresChart(section, color) {
    const labels = chartData.tests.map(t => t.testName);
    const sectionKey = `${section}Score`;
    // Cap section scores at 36
    const scores = chartData.tests.map(t => Math.min(36, t[sectionKey]));
    // Calculate composite as average of section scores, capped at 36
    const compositeScores = chartData.tests.map(t => {
        return Math.min(36, Math.round((Math.min(36, t.englishScore) + Math.min(36, t.mathScore) + Math.min(36, t.readingScore)) / 3));
    });

    const sectionLabel = section.charAt(0).toUpperCase() + section.slice(1);

    currentChart = new Chart(document.getElementById('mainChart'), {
        type: 'bar',
        data: {
            labels: labels,
            datasets: [
                {
                    label: sectionLabel,
                    data: scores,
                    backgroundColor: color,
                    borderRadius: 6,
                    barThickness: 60,
                    order: 2
                },
                {
                    label: 'Composite',
                    data: compositeScores,
                    type: 'line',
                    borderColor: '#1e3a5f',
                    backgroundColor: 'rgba(30, 58, 95, 0.1)',
                    borderWidth: 3,
                    tension: 0.4,
                    fill: false,
                    pointRadius: 5,
                    pointHoverRadius: 7,
                    pointBackgroundColor: '#1e3a5f',
                    order: 1
                }
            ]
        },
        options: {
            responsive: true,
            maintainAspectRatio: false,
            layout: {
                padding: {
                    top: 10,
                    bottom: 0,
                    left: 0,
                    right: 0
                }
            },
            scales: {
                y: {
                    beginAtZero: true,
                    max: 36,
                    ticks: { 
                        stepSize: 6,
                        padding: 5
                    },
                    grid: { color: '#e5e7eb' }
                },
                x: {
                    grid: { display: false }
                }
            },
            plugins: {
                legend: {
                    display: true,
                    position: 'bottom',
                    labels: {
                        usePointStyle: true,
                        padding: 15
                    }
                }
            }
        },
        plugins: [{
            id: 'barLabels',
            afterDatasetsDraw: (chart) => {
                const ctx = chart.ctx;
                // Only label the bar dataset (index 0)
                const dataset = chart.data.datasets[0];
                const meta = chart.getDatasetMeta(0);
                meta.data.forEach((bar, index) => {
                    const data = dataset.data[index];
                    ctx.fillStyle = '#1e293b';
                    ctx.font = 'bold 14px sans-serif';
                    ctx.textAlign = 'center';
                    ctx.fillText(data, bar.x, bar.y - 5);
                });
            }
        }]
    });
}

// Render accuracy statistics
function renderAccuracyStats(sections) {
    // English
    document.getElementById('englishCorrect').textContent = sections.english.correct;
    document.getElementById('englishIncorrect').textContent = sections.english.incorrect;
    document.getElementById('englishAccuracy').textContent = sections.english.accuracy;

    // Math
    document.getElementById('mathCorrect').textContent = sections.math.correct;
    document.getElementById('mathIncorrect').textContent = sections.math.incorrect;
    document.getElementById('mathAccuracy').textContent = sections.math.accuracy;

    // Reading
    document.getElementById('readingCorrect').textContent = sections.reading.correct;
    document.getElementById('readingIncorrect').textContent = sections.reading.incorrect;
    document.getElementById('readingAccuracy').textContent = sections.reading.accuracy;
}

// Render difficulty charts
function renderDifficultyCharts(sections) {
    ['english', 'math', 'reading'].forEach(section => {
        ['easy', 'medium', 'hard'].forEach(difficulty => {
            const data = sections[section].difficulty[difficulty];
            const canvasId = `${section}${difficulty.charAt(0).toUpperCase() + difficulty.slice(1)}Chart`;
            
            createDifficultyDonut(canvasId, data.correct, data.incorrect);
        });
    });
}

// Create a single difficulty donut chart
function createDifficultyDonut(canvasId, correct, incorrect) {
    const ctx = document.getElementById(canvasId);
    if (!ctx) {
        console.error('Canvas not found:', canvasId);
        return;
    }

    const total = correct + incorrect;
    const hasData = total > 0;

    new Chart(ctx, {
        type: 'doughnut',
        data: {
            datasets: [{
                data: hasData ? [correct, incorrect] : [1],
                backgroundColor: hasData ? ['#2563eb', '#e5e7eb'] : ['#e5e7eb'],
                borderWidth: 0
            }]
        },
        options: {
            responsive: true,
            maintainAspectRatio: true,
            cutout: '75%',
            plugins: {
                legend: { display: false },
                tooltip: { enabled: true }
            }
        },
        plugins: [{
            id: 'centerText',
            afterDraw: (chart) => {
                if (!hasData) return;

                const ctx = chart.ctx;
                const centerX = chart.width / 2;
                const centerY = chart.height / 2;
                
                ctx.save();
                ctx.font = 'bold 16px sans-serif';
                ctx.fillStyle = '#1e293b';
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.fillText(`${correct}`, centerX, centerY - 6);
                
                ctx.font = '12px sans-serif';
                ctx.fillStyle = '#64748b';
                ctx.fillText('correct', centerX, centerY + 10);
                ctx.restore();
            }
        }]
    });
}

// Render domain accuracy bars
function renderDomainBars(sections) {
    ['english', 'math', 'reading'].forEach(section => {
        const container = document.getElementById(`${section}Domains`);
        const domains = sections[section].domains;
        
        if (Object.keys(domains).length === 0) {
            container.innerHTML = '<p style="text-align: center; color: #64748b;">No domain data available</p>';
            return;
        }

        // Sort domains by accuracy
        const sortedDomains = Object.entries(domains).sort((a, b) => {
            const accA = a[1].total > 0 ? (a[1].correct / a[1].total) * 100 : 0;
            const accB = b[1].total > 0 ? (b[1].correct / b[1].total) * 100 : 0;
            return accB - accA;
        });

        container.innerHTML = sortedDomains.map(([domain, data]) => {
            const percentage = data.total > 0 ? Math.round((data.correct / data.total) * 100) : 0;
            
            return `
                <div class="domain-row">
                    <div class="domain-name">${domain}</div>
                    <div class="domain-bar-container">
                        <div class="domain-bar">
                            <div class="domain-bar-fill" style="width: ${percentage}%">
                                ${percentage > 15 ? `<span class="domain-bar-text">${data.correct}/${data.total}</span>` : ''}
                            </div>
                        </div>
                    </div>
                    <div class="domain-percentage">${percentage}%</div>
                </div>
            `;
        }).join('');
    });
}

(function(){
        // Initialize all info-tip instances
        document.querySelectorAll('.info-tip').forEach((tip)=>{
            const btn = tip.querySelector('.info-btn');
            const bubble = tip.querySelector('.tooltip');
            if(!btn || !bubble) return;

            // Put text from data-tooltip into the bubble once
            bubble.textContent = tip.dataset.tooltip || '';

            // Click/tap toggles (mobile + desktop)
            btn.addEventListener('click', (e)=>{
            e.stopPropagation();
            const open = tip.classList.toggle('is-open');
            btn.setAttribute('aria-expanded', open ? 'true' : 'false');
            });

            // Close on outside click
            document.addEventListener('click', (e)=>{
            if(!tip.contains(e.target)){
                tip.classList.remove('is-open');
                btn.setAttribute('aria-expanded','false');
            }
            });

            // Close on ESC
            document.addEventListener('keydown', (e)=>{
            if(e.key === 'Escape'){
                tip.classList.remove('is-open');
                btn.setAttribute('aria-expanded','false');
            }
            });
        });
        })();

// Utility functions
function showLoading() {
    document.getElementById('loading').style.display = 'flex';
}

function hideLoading() {
    document.getElementById('loading').style.display = 'none';
}

function showNoData() {
    document.getElementById('noData').style.display = 'block';
}

function handleLogout() {
    if (confirm('Are you sure you want to logout?')) {
        Auth.logout();
    }
}